<template>
    <div :class="{ 'pointer-events-none opacity-40': disabled }">
        <!-- Accordion header -->
        <button type="button" class="flex w-full cursor-pointer items-center gap-2.5 text-left outline-none focus:outline-none" @click="toggleOpen">
            <span class="flex size-7 shrink-0 items-center justify-center rounded-md bg-primary/10">
                <SparklesIcon class="size-3.5 text-primary" aria-hidden="true" />
            </span>
            <span class="flex-1 text-base text-gray-700 md:text-sm">{{
                t('pages.product_detail_page.ai_upload.regenerate.style_additions_title')
            }}</span>
            <ChevronDownIcon
                class="size-4 shrink-0 text-gray-400 transition-transform duration-200"
                :class="{ 'rotate-180': isOpen }"
                aria-hidden="true"
            />
        </button>

        <Collapsible :open="isOpen">
            <div class="mt-3 flex flex-col gap-3">
                <!-- Pills -->
                <div class="flex flex-wrap gap-2">
                    <button
                        v-for="addition in additions"
                        :key="addition.id"
                        type="button"
                        :disabled="disabled || http.processing || session.usedAdditionIds.includes(addition.id)"
                        class="flex items-center gap-1.5 rounded-full border px-3.5 py-2.5 text-base transition-all disabled:pointer-events-none disabled:opacity-50 md:px-3 md:py-1.5 md:text-sm"
                        :class="
                            selectedAdditionId === addition.id
                                ? 'border-gray-900 bg-gray-900 text-white'
                                : 'border-border bg-background text-foreground hover:border-gray-900 hover:text-gray-900'
                        "
                        @click="toggleAddition(addition.id)"
                    >
                        <CheckIcon v-if="session.usedAdditionIds.includes(addition.id)" class="size-3.5 shrink-0" aria-hidden="true" />
                        <PlusIcon v-else class="size-3.5 shrink-0" aria-hidden="true" />
                        <img v-if="addition.icon_url" :src="addition.icon_url" :alt="addition.label" class="size-4 shrink-0 object-contain" />
                        <span>{{ addition.label }}</span>
                        <Tooltip v-if="addition.description" :content="addition.description" side="top">
                            <InfoIcon
                                class="size-[18px] shrink-0 transition-colors"
                                :class="selectedAdditionId === addition.id ? 'text-white/90' : 'text-gray-500'"
                                aria-hidden="true"
                                @click.stop
                            />
                        </Tooltip>
                    </button>
                </div>

                <!-- Generate button (appears when a pill is selected, height-animated to prevent layout jumps) -->
                <div
                    class="grid transition-all duration-300 ease-out"
                    :class="selectedAdditionId !== undefined ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'"
                >
                    <div class="overflow-hidden">
                        <div class="space-y-1.5 pt-0.5">
                            <Button
                                :label="t('pages.product_detail_page.ai_upload.regenerate.style_additions_generate', { label: stableAdditionLabel })"
                                :leading-icon="SparklesIcon"
                                :loading="http.processing"
                                :disabled="disabled || http.processing"
                                class="h-14 w-full border border-gray-300 bg-white text-base text-gray-700 hover:border-gray-400 hover:bg-gray-50 disabled:hover:border-gray-300 disabled:hover:bg-white md:h-auto md:text-sm"
                                @click="submit()"
                            />
                            <p class="text-center text-sm text-gray-400 md:text-xs">
                                {{ t('pages.product_detail_page.ai_upload.regenerate.style_additions_cost_notice') }}
                            </p>
                        </div>
                    </div>
                </div>

                <p v-if="errorMessage" class="text-destructive flex items-center gap-1 text-base md:text-sm">
                    <CircleAlertIcon class="size-4 shrink-0" />
                    {{ errorMessage }}
                </p>
            </div>
        </Collapsible>
    </div>
</template>

<script setup lang="ts">
import Button from '@/components/buttons/Button.vue';
import Collapsible from '@/components/collapsible/Collapsible.vue';
import Tooltip from '@/components/tooltips/Tooltip.vue';
import { useAiCreditsRemaining } from '@/composables/ai-upload/useAiCreditsRemaining';
import { useAppearanceComposable } from '@/composables/appearance/AppearanceComposable';
import { useAiGenerationHistoryStore } from '@/stores/ai-upload/AiGenerationHistoryStore';
import { useAiImageGenerationSessionStore } from '@/stores/ai-upload/AiImageGenerationSessionStore';
import { useAiPersonalizationStore } from '@/stores/ai-upload/AiPersonalizationStore';
import AiProductPageStyleAdditionController from '@/types/wayfinder/actions/App/Http/Controllers/Web/ProductPage/AiProductPageStyleAdditionController';
import type { AiStyleAdditionObject } from '@/types/generated';
import { useHttp } from '@inertiajs/vue3';
import { CheckIcon, ChevronDownIcon, CircleAlertIcon, InfoIcon, PlusIcon, SparklesIcon } from '@lucide/vue';
import _ from 'lodash';
import { useI18n } from 'vue-i18n';
import { computed, ref, watch } from 'vue';

type RegenerateBlockProps = {
    disabled?: boolean;
    additions: AiStyleAdditionObject[];
};

const props = withDefaults(defineProps<RegenerateBlockProps>(), {
    disabled: false,
});

const { t } = useI18n();
const session = useAiImageGenerationSessionStore();
const historyStore = useAiGenerationHistoryStore();
const personalizationStore = useAiPersonalizationStore();
const { pendingCredits, isUserDailyLimitExceeded, hoursUntilNextCredit } = useAiCreditsRemaining();
const { isTabletUp } = useAppearanceComposable();

type FeedbackResponse = {
    correlationId: string;
    token: string;
    creditsRemaining?: number;
    isUserDailyLimitExceeded?: boolean;
    hoursUntilNextCredit?: number | null;
};

const http = useHttp<Record<string, never>, FeedbackResponse>();

const isOpen = ref<boolean>(false);
const selectedAdditionId = ref<number | undefined>(undefined);
const errorMessage = ref<string | undefined>(undefined);

watch(
    () => personalizationStore.isOpen,
    (isPersonalizationOpen: boolean) => {
        if (isPersonalizationOpen) {
            isOpen.value = false;
        }
    },
);

watch(
    () => props.disabled,
    (isDisabled: boolean, wasDisabled: boolean) => {
        if (isDisabled && isOpen.value) {
            isOpen.value = false;
        }
        if (!isDisabled && wasDisabled) {
            selectedAdditionId.value = undefined;
        }
    },
);

const selectedAddition = computed<AiStyleAdditionObject | undefined>(() =>
    props.additions.find((addition: AiStyleAdditionObject) => addition.id === selectedAdditionId.value),
);

// Keeps the last non-undefined label so the button text stays stable during the
// close animation when selectedAdditionId is cleared before the animation finishes.
const stableAdditionLabel = ref<string | undefined>(undefined);

watch(selectedAddition, addition => {
    if (addition !== undefined) {
        stableAdditionLabel.value = addition.label;
    }
});

const regenerateCorrelationId = computed<string | undefined>(() => {
    if (session.correlationId !== undefined) {
        return session.correlationId;
    }

    return _.orderBy(historyStore.entries, 'createdAt', 'desc')[0]?.id;
});

function toggleOpen(): void {
    if (props.disabled) {
        return;
    }
    isOpen.value = !isOpen.value;

    // The two blocks only take turns on a phone, where each is a CTA that can be opened again. From
    // `md` up the text block has no CTA of its own — it is the panel's permanent text section — so
    // closing it here would take the styling controls away for good.
    if (isOpen.value && !isTabletUp.value) {
        personalizationStore.isOpen = false;
    }
}

function toggleAddition(id: number): void {
    selectedAdditionId.value = selectedAdditionId.value === id ? undefined : id;
    errorMessage.value = undefined;
}

async function submit(): Promise<void> {
    if (http.processing || regenerateCorrelationId.value === undefined || selectedAdditionId.value === undefined) {
        return;
    }

    const additionId = selectedAdditionId.value;
    errorMessage.value = undefined;

    try {
        const correlationId = regenerateCorrelationId.value;

        await http
            .transform(() => ({ correlation_id: correlationId, ai_style_addition_id: additionId }))
            .post(AiProductPageStyleAdditionController().url, {
                onSuccess: (data: FeedbackResponse) => {
                    const additionLabel = props.additions.find((addition: AiStyleAdditionObject) => addition.id === additionId)?.label;
                    session.addUsedAdditionId(additionId);
                    selectedAdditionId.value = undefined;
                    historyStore.snapshotIfCompleted();
                    historyStore.resetSelection();
                    session.activate(data.correlationId, data.token);

                    if (additionLabel !== undefined) {
                        session.setActiveAdditionLabel(additionLabel);
                    }

                    if (data.creditsRemaining !== undefined) {
                        pendingCredits.value = data.creditsRemaining;
                    }

                    if (data.isUserDailyLimitExceeded !== undefined) {
                        isUserDailyLimitExceeded.value = data.isUserDailyLimitExceeded;
                        hoursUntilNextCredit.value = data.hoursUntilNextCredit ?? undefined;
                    }
                },
                onError: (errors: Record<string, string>) => {
                    errorMessage.value = Object.values(errors)[0] ?? t('pages.product_detail_page.ai_upload.regenerate.error_generic');
                },
                onHttpException: () => {
                    errorMessage.value = t('pages.product_detail_page.ai_upload.regenerate.error_generic');
                },
                onNetworkError: () => {
                    errorMessage.value = t('pages.product_detail_page.ai_upload.regenerate.error_generic');
                },
            });
    } catch {
        // Errors are handled by the callbacks above.
    }
}
</script>
